/**
 * سربرگِ «سلسله‌مراتبِ تگ‌ها» — تنها جایی که رابطه‌ی مادر/فرزندِ دسته‌ها
 * تعریف می‌شود (مایگریشن 0084).
 *
 * ═══ چرا این ابزار لازم شد ═══
 *
 * ردیفِ دسته‌های صفحه‌ی کشف تا امروز فقط یک حدسِ آماری داشت: هر رخداد به
 * پرتکرارترین تگِ خودش «تعلق» می‌گرفت، پس زیرشاخه‌ای که همیشه کنارِ
 * سرشاخه‌اش می‌آید بالا نمی‌آمد (نگاه کن به core/lib/tagCounts). آن حدس تا
 * جایی درست است که داده همکاری کند — «راک» با ۹۳ رخداد که فقط نصفشان تگِ
 * «موسیقی» هم دارند، از دستش در می‌رود و کنارِ مادرش می‌نشیند.
 *
 * این‌جا همان رابطه **صریح** گفته می‌شود: تا وقتی مادر روی ردیف است،
 * فرزندهایش نمی‌آیند؛ به‌محضِ اینکه کاربر روی مادر فیلتر کند، فرزندها
 * همان‌جا باز می‌شوند. یعنی ردیف دو طبقه می‌شود.
 *
 * ═══ کنار گذاشتن ═══
 *
 * تگی که کنار گذاشته می‌شود از فرم‌ها و از ردیفِ دسته‌های کشف بیرون می‌رود،
 * ولی هیچ ردیفی پاک نمی‌شود و همان دکمه برش می‌گرداند (مایگریشن 0086).
 * برای همین تاییدِ دوم ندارد: پنجره‌ی «مطمئنی؟» برای کاری است که برگشت
 * ندارد، و این یکی دقیقاً برعکسش است.
 *
 * رخدادی که با این کار بی‌تگ می‌ماند زیرِ «سایر» پیدا می‌شود — و «سایر» را
 * که کنار بگذاری، آن پناهگاه هم می‌رود.
 *
 * این تنها سربرگی است که تگِ کنارگذاشته را **می‌بیند** — بقیه‌ی اپ اصلاً
 * برنمی‌گرداندش. پس این ردیف‌ها همیشه این‌جا می‌مانند، حتی وقتی فیلترِ «فقط
 * تگ‌هایی که رخداد دارند» روشن است؛ وگرنه راهِ برگرداندنشان هم گم می‌شد.
 *
 * ═══ ترتیبِ ردیفِ کشف ═══
 *
 * سوییچِ بالای صفحه سراسری است، نه پیش‌نمایش: همان لحظه برای همه عوض
 * می‌شود (مایگریشن 0088). دلیلِ وجودش با عدد گفتنی است — با قاعده‌ی
 * «پرتکرارترین»، روی دادهٔ اتاوا ۱۵ تا از ۱۷ تگِ فرزند صفر امتیاز
 * می‌گیرند و «راک» با ۱۱۳ رخداد فقط ۱ امتیاز دارد، چون تقریباً هر
 * رخدادِ راک تگِ «موسیقی» را هم دارد. «کم‌تکرارترین» دقیقاً برعکسش است.
 * کدامش بهتر است را فقط با دیدنِ هر دو روی شهرِ واقعی می‌شود فهمید.
 *
 * ═══ چرا شمارِ رخدادها کنارِ هر تگ هست ═══
 *
 * بدونِ آن، این صفحه ۱۳۹ ردیفِ هم‌شکل است و هیچ راهی نیست بفهمی کدام تگ
 * اصلاً ارزشِ سلسله‌مراتب دارد. شمارش در خودِ دیتابیس انجام می‌شود
 * (`admin_tag_usage`، مایگریشن 0087) و نه این‌جا — گرفتنِ کلِ `event_tags`
 * و شمردنش در مرورگر به سقفِ ردیفِ PostgREST می‌خورد و نصفِ تگ‌ها را «۰»
 * نشان می‌داد.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { settingsRepo, tagRepo } from '@/core/data/miscRepo';
import type { MergePick } from '@/core/lib/tagCounts';
import { adminRepo } from '@/core/data/adminRepo';
import { reportError } from '@/core/lib/report';
import type { Tag } from '@/core/domain/types';

/** فرزندهای مستقیمِ هر تگ. */
const childrenOf = (tags: Tag[]) => {
  const out = new Map<number, Tag[]>();
  for (const t of tags) {
    if (t.parentId == null) continue;
    const list = out.get(t.parentId) ?? [];
    list.push(t);
    out.set(t.parentId, list);
  }
  return out;
};

/**
 * تگ‌هایی که نمی‌شود مادرِ `tag` باشند: خودش و هر نوه‌ونتیجه‌اش.
 *
 * دیتابیس هم همین را رد می‌کند، ولی گزینه‌ای که انتخابش همیشه خطا می‌دهد
 * نباید اصلاً توی فهرست باشد.
 */
function forbiddenParents(tag: Tag, kids: Map<number, Tag[]>): Set<number> {
  const out = new Set<number>([tag.id]);
  const stack = [tag.id];
  while (stack.length) {
    const id = stack.pop()!;
    for (const c of kids.get(id) ?? []) {
      if (out.has(c.id)) continue;
      out.add(c.id);
      stack.push(c.id);
    }
  }
  return out;
}

export function TagsTab() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [onlyUsed, setOnlyUsed] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // کنارگذاشته‌ها هم می‌آیند — نگاه کن به سرِ فایل. کلیدِ کوئری هم برای همین
  // جداست از ['tags','all']ِ بقیه‌ی اپ، که همان فهرست بدونِ آن‌هاست.
  const tags = useQuery({ queryKey: ['tags', 'withArchived'], queryFn: tagRepo.listWithArchived });
  const usage = useQuery({ queryKey: ['tagUsage'], queryFn: adminRepo.tagUsage });
  const pick = useQuery({
    queryKey: ['setting', 'tagMergePick'], queryFn: settingsRepo.tagMergePick,
  });

  const setPick = useMutation({
    mutationFn: (v: MergePick) => adminRepo.setTagMergePick(v),
    onSuccess: (_r, v) => {
      setErr(null);
      setDone(v === 'most'
        ? 'ترتیب: هر رخداد به پرتکرارترین تگِ خودش رأی می‌دهد'
        : 'ترتیب: هر رخداد به کم‌تکرارترین تگِ خودش رأی می‌دهد');
      qc.invalidateQueries({ queryKey: ['setting', 'tagMergePick'] });
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg);
      reportError('admin.setTagMergePick', e);
    },
  });

  const all = useMemo(() => tags.data ?? [], [tags.data]);
  const kids = useMemo(() => childrenOf(all), [all]);
  const count = (id: number) => usage.data?.get(id) ?? 0;

  const setParent = useMutation({
    mutationFn: ({ id, parentId }: { id: number; parentId: number | null }) =>
      adminRepo.setTagParent(id, parentId),
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ['tags'] }); },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg);
      reportError('admin.setTagParent', e);
    },
  });

  /**
   * کنار گذاشتن و برگرداندن، هر دو با همین یکی.
   *
   * پیامِ موفقیت عمداً می‌گوید چند آیتم تحتِ تاثیرند: «کنار گذاشته شد» برای
   * تگی که ۹۳ رخداد دارد کم است، حتی وقتی برگشت‌پذیر باشد.
   */
  const archive = useMutation({
    mutationFn: ({ tag, on }: { tag: Tag; on: boolean }) =>
      adminRepo.setTagArchived(tag.id, on),
    onSuccess: (_r, { tag, on }) => {
      setErr(null);
      const n = count(tag.id);
      setDone(on
        ? `«${tag.labelFa}» کنار گذاشته شد${n
          ? ` · ${n} رخداد دیگر زیرِ آن پیدا نمی‌شوند؛ آن‌هایی که تگِ دیگری ندارند`
            + ' زیرِ «سایر» می‌روند'
          : ''}`
        : `«${tag.labelFa}» برگشت`);
      qc.invalidateQueries({ queryKey: ['tags'] });
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg);
      reportError('admin.setTagArchived', e);
    },
  });

  /**
   * ترتیب: سرشاخه‌ها بر اساسِ شمارِ رخداد، و بلافاصله بعدِ هر کدام
   * فرزندهایش. یعنی همان چیزی که کاربر روی ردیفِ کشف می‌بیند، ولی باز.
   */
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const match = (t: Tag) => !needle
      || t.slug.includes(needle)
      || t.labelEn.toLowerCase().includes(needle)
      || t.labelFa.includes(needle);

    const keep = (t: Tag) => (!onlyUsed || count(t.id) > 0);
    const out: { tag: Tag; depth: number }[] = [];
    const walk = (t: Tag, depth: number) => {
      out.push({ tag: t, depth });
      for (const c of (kids.get(t.id) ?? []).sort((a, b) => count(b.id) - count(a.id))) {
        walk(c, depth + 1);
      }
    };
    all
      .filter((t) => t.parentId == null)
      .sort((a, b) => count(b.id) - count(a.id) || a.slug.localeCompare(b.slug))
      .forEach((t) => walk(t, 0));

    // جستجو و «فقط استفاده‌شده‌ها» بعد از چیدنِ درخت اعمال می‌شوند: فرزندی
    // که خودش جواب می‌دهد باید بماند حتی اگر مادرش نه. تگِ کنارگذاشته هم از
    // این فیلتر رد می‌شود، چون این تنها جایی است که می‌شود برش گرداند.
    return out.filter(({ tag }) => match(tag)
      && (keep(tag) || tag.parentId != null || tag.archivedAt));
  }, [all, kids, q, onlyUsed, usage.data]);

  if (tags.isLoading) return <p className="dim">…</p>;
  if (tags.error) return <p className="err">فهرستِ تگ‌ها نیامد.</p>;

  return (
    <div className="card">
      <h2>سلسله‌مراتبِ تگ‌ها</h2>
      <p className="dim" style={{ marginBottom: 10 }}>
        تا وقتی تگِ مادر روی ردیفِ کشف است، فرزندهایش نشان داده نمی‌شوند؛ با فیلتر
        کردن روی مادر، همان‌جا باز می‌شوند. «—» یعنی این تگ مادر ندارد.
      </p>
      <p className="dim" style={{ marginBottom: 10 }}>
        «کنار بگذار» چیزی را پاک نمی‌کند: تگ از فرم‌ها و از ردیفِ دسته‌ها بیرون
        می‌رود و همان دکمه برش می‌گرداند. رخدادی که تگِ دیگری نداشته باشد زیرِ
        «سایر» پیدا می‌شود.
      </p>
      <p className="dim" style={{ marginBottom: 10 }}>
        ستونِ «رخداد» شمارِ <b>کلِ دیتابیس</b> است — همه‌ی شهرها، گذشته‌ها هم توش.
        عددی که کاربر کنارِ آیکون می‌بیند از این کوچک‌تر است، چون فقط شهرِ خودش و
        رخدادهای نگذشته را می‌شمارد.
      </p>

      {/* ترتیبِ ردیفِ کشف. عمداً بالای فهرست است و نه ته صفحه: کسی که آمده
          سلسله‌مراتب را درست کند، همین‌جا باید ببیند قاعده‌ی ترتیب چیست. */}
      <div className="filters" style={{ marginBottom: 10 }}>
        <span className="dim">ترتیبِ ردیفِ کشف — رأیِ هر رخداد به کدام تگِ خودش برسد:</span>
        <div className="chiprow">
          {([
            ['most', 'پرتکرارترین', 'سرشاخه‌ها بالا می‌آیند؛ زیرشاخه‌ای که همیشه کنارِ مادرش'
              + ' می‌آید امتیاز نمی‌گیرد'],
            ['least', 'کم‌تکرارترین', 'هر رخداد به مشخص‌ترین تگِ خودش می‌رسد؛ ردیف پر می‌شود'
              + ' از زیرشاخه‌ها'],
          ] as const).map(([v, label, hint]) => (
            <button key={v} type="button" title={hint}
              className={`chip${(pick.data ?? 'most') === v ? ' on' : ''}`}
              disabled={setPick.isPending || pick.isLoading}
              onClick={() => { setDone(null); setPick.mutate(v); }}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <p className="dim" style={{ marginBottom: 10 }}>
        این انتخاب سراسری است — همان لحظه برای همه عوض می‌شود، نه فقط این صفحه.
        عددی که کنارِ آیکون‌ها دیده می‌شود عوض نمی‌شود؛ فقط ترتیبشان.
      </p>

      <div className="filters" style={{ marginBottom: 10 }}>
        <input placeholder="جستجوی تگ…" value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="dim" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <input type="checkbox" checked={onlyUsed}
            onChange={(e) => setOnlyUsed(e.target.checked)} />
          فقط تگ‌هایی که رخداد دارند
        </label>
        <span className="spacer" />
        <span className="dim">{rows.length} از {all.length}</span>
      </div>

      {err ? <p className="err" style={{ marginBottom: 8 }}>{err}</p> : null}
      {done ? <p className="dim" style={{ marginBottom: 8 }}>{done}</p> : null}

      <div className="scrollx">
        <table className="tbl">
          <thead>
            <tr>
              <th>تگ</th>
              <th>slug</th>
              <th>نشان</th>
              <th className="num">رخداد</th>
              <th>مادر</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map(({ tag, depth }) => {
              const banned = forbiddenParents(tag, kids);
              const archived = !!tag.archivedAt;
              return (
                <tr key={tag.id} style={archived ? { opacity: 0.6 } : undefined}>
                  <td style={{ paddingInlineStart: 8 + depth * 18 }}>
                    {depth > 0 ? <span className="dim">↳ </span> : null}
                    <span style={archived ? { textDecoration: 'line-through' } : undefined}>
                      {tag.labelFa}
                    </span>
                    <span className="dim"> · {tag.labelEn}</span>
                    {archived ? <span className="dim"> · کنار گذاشته شده</span> : null}
                  </td>
                  <td className="dim">{tag.slug}</td>
                  <td className="dim">{tag.emoji}</td>
                  <td className="num">{count(tag.id) || '—'}</td>
                  <td>
                    <select
                      value={tag.parentId ?? ''}
                      disabled={setParent.isPending}
                      onChange={(e) => setParent.mutate({
                        id: tag.id,
                        parentId: e.target.value === '' ? null : Number(e.target.value),
                      })}>
                      <option value="">— بدونِ مادر —</option>
                      {all
                        // مادرِ کنارگذاشته یعنی فرزند هم عملاً بی‌مادر می‌شود
                        // (نگاه کن به مایگریشن 0086)، پس پیشنهادش نمی‌کنیم —
                        // مگر همین الان مادرش باشد، که آن‌وقت باید دیده شود.
                        .filter((p) => !banned.has(p.id) && (!p.archivedAt || p.id === tag.parentId))
                        .sort((a, b) => count(b.id) - count(a.id) || a.slug.localeCompare(b.slug))
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.labelFa} ({p.slug})
                          </option>
                        ))}
                    </select>
                  </td>
                  <td>
                    <button type="button" className="btn ghost"
                      title={archived
                        ? 'برگرداندنِ تگ به فرم‌ها و به ردیفِ دسته‌ها'
                        : 'تگ می‌ماند ولی هیچ‌جای اپ دیده نمی‌شود'}
                      disabled={archive.isPending}
                      onClick={() => { setDone(null); archive.mutate({ tag, on: !archived }); }}>
                      {archived ? 'برگردان' : 'کنار بگذار'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
}
